import { CLIENT_LEGACY_STATES } from './clientLegacyState';
import { CLIENT_SORTS, CLIENT_TABS } from './clientListView';
import type { ClientLegacyState } from './clientLegacyState';
import type { ClientSort, ClientTab } from './clientListView';
import type { CalendarFilter } from './reminderCalendar';

/** The reminder tab renders either the list or the calendar. */
export type ClientListMode = 'list' | 'calendar';

const CLIENT_LIST_MODES: ClientListMode[] = ['list', 'calendar'];
const CALENDAR_FILTERS: CalendarFilter[] = ['reminder', 'birthday', 'all'];

/**
 * The calendar filter is stored under a versioned field. The first version
 * defaulted to 'reminder', and nearly every stored value is that default rather
 * than a choice, so the switch to 'all' reads only the new field: the old one
 * is ignored and everyone lands on 全部 once, then keeps whatever they pick.
 */
const CALENDAR_FILTER_FIELD = 'calendarFilterV2';

const readCalendarFilter = (view: { [field: string]: unknown } | undefined): CalendarFilter =>
  CALENDAR_FILTERS.includes(view?.[CALENDAR_FILTER_FIELD] as CalendarFilter)
    ? (view?.[CALENDAR_FILTER_FIELD] as CalendarFilter)
    : 'all';

/**
 * Page-position persistence, mirroring V1 (clientPage.js:424-466, 769-784).
 *
 * V1 stores where the adviser was so that coming back from a client's
 * dashboard, portfolio or factsheet lands on the same page, search and list
 * state instead of resetting to the top of an unfiltered list.
 *
 * The four keys V1 owns are reused verbatim so the two pages stay
 * interchangeable while both exist — V1 written state restores in ClientV2 and
 * vice versa. `clientState` only ever carries a legacy list state, which is the
 * same vocabulary in both pages. What V1 cannot express (the v46 tab row, and a
 * sort that is independent of the list state) lives in one extra V2-only key.
 *
 * `selectedLuckyClientObj` is stored as V1 stores it, but only ever as an
 * `{ _id }` stub: those are the only two reads V1 makes of it
 * (clientPage.js:461, 1647), so a V1 session restoring a ClientV2-written value
 * still narrows to the right client.
 *
 * Deliberately NOT persisted:
 * - `virusRelatedClients` — the covid state is excluded on the user's
 *   instruction.
 * - `scrollCoordinate` / `changeStateCord` — V1 scroll restoration for its
 *   accordion list; ClientV2 pages to the client and opens the drawer instead.
 * - the sidebar filters: V1 does not persist `filterObject` either.
 */
export const CURRENT_PAGE_KEY = 'currentPage';
export const SEARCH_TERM_KEY = 'searchTerm';
export const CLIENT_STATE_KEY = 'clientState';
export const CLIENT_ID_KEY = 'clientId';
export const PRE_SELECTED_IDS_KEY = 'preSelectedClientIds';
export const LUCKY_CLIENT_KEY = 'selectedLuckyClientObj';
export const CLIENT_V2_VIEW_KEY = 'clientv2ViewState';

/**
 * V1's 招募 view. It rides on `clientState` like a list state
 * (Recruitment.js:116 writes it, clientPage.js:441 reads it back), but in
 * ClientV2's vocabulary it is a TAB — so it is deliberately absent from
 * CLIENT_LEGACY_STATES and translated at the boundary instead, in both
 * directions. Without the translation the 招募中的客戶 button lands an opted-in
 * adviser on an unfiltered list, and the write below would then erase the value
 * so V1 could not recover it either.
 */
export const RECRUITMENT_STATE = 'recruitment';

/**
 * 招募模式 — whether the whole page is in the recruitment workspace, as opposed
 * to the 招募 TAB, which is a filter of the ordinary page.
 *
 * ⚠️ Its own key, deliberately NOT `clientState`. That one is shared with
 * Client **V1** (`views/Advisor/Client/partials/clientPage.js` reads it, as
 * does `views/Advisor/Business/component.js`), so a mode flag written there
 * would change what V1 does on its next load. The two states are related but
 * they are not the same state, and only one of them is V1's business.
 */
export const RECRUIT_MODE_KEY = 'clientv2RecruitMode';

/**
 * `unlocked` is the campaign-code gate (FEATURE_RECRUITMODE, resolved in
 * ./container). It is re-checked on every READ, not only when the mode is
 * entered: an adviser whose code lapses while the flag sits in localStorage
 * would otherwise come back to an amber page with no way out of it.
 */
export const readRecruitMode = (unlocked: boolean): boolean => unlocked && readJson(RECRUIT_MODE_KEY) === true;

export const writeRecruitMode = (on: boolean): void => {
  if (on) writeJson(RECRUIT_MODE_KEY, true);
  else removeKey(RECRUIT_MODE_KEY);
};

export type PersistedClientView = {
  page: number;
  searchTerm: string;
  legacyState: ClientLegacyState | null;
  clientId: string | null;
  preSelectedClientIds: string[];
  /** The one client the `lucky` state narrows to, V1's selectedLuckyClient. */
  luckyClientId: string | null;
  tab: ClientTab;
  sort: ClientSort;
  /** List or calendar. The calendar only renders on the reminder tab (D1). */
  view: ClientListMode;
  /** Which kinds the calendar counts. Defaults to everything. */
  calendarFilter: CalendarFilter;
};

const readJson = (key: string): unknown => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch (error) {
    // V1 writes these with JSON.stringify, but a hand-edited or half-written
    // value must never take the page down.
    return undefined;
  }
};

const writeJson = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Private-mode quota failures are not worth a broken page.
  }
};

const removeKey = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // Storage can be denied outright (blocked third-party storage in the
    // PFPData iframe, Safari private mode), and a throw here would abort the
    // caller — including the mount effect that fetches the client list.
  }
};

export const readPersistedClientView = (): PersistedClientView => {
  const page = readJson(CURRENT_PAGE_KEY);
  const searchTerm = readJson(SEARCH_TERM_KEY);
  const legacyState = readJson(CLIENT_STATE_KEY);
  const clientId = readJson(CLIENT_ID_KEY);
  const preSelected = readJson(PRE_SELECTED_IDS_KEY);
  // V1 stores the whole client object here; only its `_id` is ever read back.
  const lucky = readJson(LUCKY_CLIENT_KEY) as { _id?: unknown } | undefined;
  const view = readJson(CLIENT_V2_VIEW_KEY) as
    | { tab?: unknown; sort?: unknown; view?: unknown; [field: string]: unknown }
    | undefined;
  // A fresh 招募 hand-off wins over the stored tab: whoever just wrote
  // `clientState` did so to say where this navigation should land.
  const isRecruitment = legacyState === RECRUITMENT_STATE;
  const storedTab = CLIENT_TABS.includes(view?.tab as ClientTab) ? (view?.tab as ClientTab) : 'all';

  return {
    page: typeof page === 'number' && page >= 0 ? Math.floor(page) : 0,
    searchTerm: typeof searchTerm === 'string' ? searchTerm : '',
    legacyState:
      !isRecruitment && CLIENT_LEGACY_STATES.includes(legacyState as ClientLegacyState)
        ? (legacyState as ClientLegacyState)
        : null,
    clientId: typeof clientId === 'string' && clientId ? clientId : null,
    preSelectedClientIds: Array.isArray(preSelected)
      ? preSelected.filter((id): id is string => typeof id === 'string')
      : [],
    luckyClientId: typeof lucky?._id === 'string' && lucky._id ? lucky._id : null,
    tab: isRecruitment ? 'recruit' : storedTab,
    sort: CLIENT_SORTS.includes(view?.sort as ClientSort) ? (view?.sort as ClientSort) : 'default',
    view: CLIENT_LIST_MODES.includes(view?.view as ClientListMode) ? (view?.view as ClientListMode) : 'list',
    calendarFilter: readCalendarFilter(view)
  };
};

/**
 * V1's one-shot "narrow the next getClient" key (store/client/api.js reads it,
 * then deletes it). ClientV2 never sets it and never wants it applied, so it
 * clears any orphan on entry — see the mount effect in component.tsx.
 */
export const LEGACY_CLIENT_SORT_KEY = 'clientSortBy';

export const clearLegacyClientSort = (): void => removeKey(LEGACY_CLIENT_SORT_KEY);

/**
 * Land the next ClientV2 visit on a given tab, as if the adviser had just
 * tapped that chip.
 *
 * For hand-offs from OUTSIDE the page — today the 業績 page's 跟進中個案
 * drill-down. It exists so those hand-offs do not narrow the FETCH instead:
 * `getClient?sortBy=…` answers with a subset, and that subset replaces
 * `client.clientData`, which every stat tile, tab count, sidebar option and
 * empty-state message is computed from. A narrowed fetch makes the whole page
 * describe 25 clients while the 全部 chip stays lit, and leaves it that way
 * until some later fetch happens to be an unnarrowed one — which is why the
 * only cure an adviser could find was pressing 重新整理.
 *
 * "As if tapped" is the whole contract, and it takes four keys, not one.
 * `handleTab` also drops the legacy state and returns to the first page, and
 * anything still narrowing the list would silently narrow this one too:
 *
 * - a legacy state (`clientState`) left over from an A.I. 精選 or a coverage-gap
 *   entry stacks ON TOP of the tab, so the adviser asks for 跟進中個案 and gets
 *   a subset of it, with only the chip to explain the count;
 * - `currentPage` restores the page they left on — page 3 of 187 clients is
 *   past the end of 30, and the clamp lands them on the last page rather than
 *   the first, so the top of the list they asked for is off screen;
 * - a `searchTerm` still in the box filters the tab down to matches of a search
 *   they ran before they ever left the page.
 *
 * The sort is the one thing deliberately kept: it is a preference about how to
 * read a list, not a narrowing of which list, and a hand-off has no business
 * resetting it.
 */
export const requestClientV2Tab = (tab: ClientTab): void => {
  const view = readJson(CLIENT_V2_VIEW_KEY) as { sort?: unknown; view?: unknown; [field: string]: unknown } | undefined;
  const sort = CLIENT_SORTS.includes(view?.sort as ClientSort) ? (view?.sort as ClientSort) : 'default';
  // The view mode and the calendar filter are preferences about how to READ a
  // list, not a narrowing of which list — the same reason the sort survives a
  // hand-off. Dropping them here would silently undo the adviser's choice every
  // time another page sent them to a tab.
  const mode = CLIENT_LIST_MODES.includes(view?.view as ClientListMode) ? (view?.view as ClientListMode) : 'list';
  writeJson(CLIENT_V2_VIEW_KEY, { tab, sort, view: mode, [CALENDAR_FILTER_FIELD]: readCalendarFilter(view) });
  writeJson(CLIENT_STATE_KEY, tab === 'recruit' ? RECRUITMENT_STATE : '');
  writeJson(CURRENT_PAGE_KEY, 0);
  writeJson(SEARCH_TERM_KEY, '');
  removeKey(LUCKY_CLIENT_KEY);
  // A remembered 招募模式 would otherwise swallow the hand-off: in the mode the
  // list is the candidates whatever the tab says, so 跟進中個案 would land on
  // the recruitment workspace. A business tab is only reachable outside the
  // mode, so "as if tapped" means leaving it. A 招募 hand-off leaves it alone.
  if (tab !== 'recruit') removeKey(RECRUIT_MODE_KEY);
};

/**
 * `clientId` is the app-wide "client being viewed" key (goToPortfolio and
 * friends read it), so a null selection leaves the stored value alone — V1's
 * `setLocalStorage` likewise only writes it when it is handed an id.
 */
export const writePersistedClientView = (view: PersistedClientView): void => {
  writeJson(CURRENT_PAGE_KEY, view.page);
  writeJson(SEARCH_TERM_KEY, view.searchTerm);
  // The 招募 tab is written back as V1's `'recruitment'` so the round trip
  // closes: an adviser who switches to 回到舊版 from this tab lands on V1's
  // recruitment tab, exactly as the reverse direction already works. An active
  // legacy state still wins — it is the narrower, more recent intent.
  writeJson(CLIENT_STATE_KEY, view.legacyState || (view.tab === 'recruit' ? RECRUITMENT_STATE : ''));
  writeJson(PRE_SELECTED_IDS_KEY, view.preSelectedClientIds);
  writeJson(CLIENT_V2_VIEW_KEY, {
    tab: view.tab,
    sort: view.sort,
    view: view.view,
    [CALENDAR_FILTER_FIELD]: view.calendarFilter
  });
  if (view.clientId) writeJson(CLIENT_ID_KEY, view.clientId);
  // V1 removes the stub when it leaves the lucky state (setLocalStorage's
  // noClientState branch), so a stale suggestion cannot come back later.
  if (view.luckyClientId) writeJson(LUCKY_CLIENT_KEY, { _id: view.luckyClientId });
  else removeKey(LUCKY_CLIENT_KEY);
};

/**
 * Forget the remembered client.
 *
 * `writePersistedClientView` deliberately never clears `clientId` — it is the
 * app-wide "client being viewed" key, and a null selection must not wipe what
 * another page put there. A DELETED client is the one case that does need
 * clearing: leaving the id behind makes the next visit try to reopen a record
 * that no longer exists, and `goToPortfolio` and friends read the same key.
 * (`deleteClientEpic` removes it too, for deletions that happen off this page.)
 */
export const clearPersistedClient = (): void => removeKey(CLIENT_ID_KEY);

/**
 * The half-finished add-client / add-policy drafts V1 drops on entry
 * (clientPage.js:479-484). Leaving them behind is what makes a new client form
 * open pre-filled with the previous, abandoned one.
 */
export const CLIENT_DRAFT_KEYS = [
  'clientPersonalData',
  'clientFactsheetDataOne',
  'clientFactsheetDataTwo',
  'clientFactsheetDataThree',
  'addRider',
  'previousPolicyData',
  'insurer'
] as const;

export const clearClientFormDrafts = (): void => {
  CLIENT_DRAFT_KEYS.forEach(removeKey);
};

/**
 * Which clients the adviser last read in the drawer's 招募 view.
 *
 * The drawer opens on 業務 the first time, always: a client marked 潛在招募 is
 * still a client, and landing on a panel with the policy tiles gone would read
 * as data loss rather than as a view. But an adviser working through their
 * recruitment list should not have to flip the switch on every single record,
 * so the choice sticks per client.
 *
 * Stored as a capped list of ids rather than a map, because presence is the
 * whole value and a list is self-pruning: the newest id goes to the front and
 * anything past RECRUIT_VIEW_MEMORY_MAX falls off the end. Nothing here is
 * authoritative — a lost entry costs one tap.
 */
export const RECRUIT_VIEW_KEY = 'clientv2RecruitView';

/** Roughly a working book's worth of recent records; well under any quota. */
const RECRUIT_VIEW_MEMORY_MAX = 100;

const readRecruitViewIds = (): string[] => {
  const stored = readJson(RECRUIT_VIEW_KEY);
  return Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : [];
};

/** True when this client's drawer should open on 招募 rather than 業務. */
export const readsAsRecruitView = (clientId: string | null | undefined): boolean =>
  Boolean(clientId) && readRecruitViewIds().includes(clientId as string);

/** Remember (or forget) that this client's drawer was left on the 招募 view. */
export const rememberRecruitView = (clientId: string | null | undefined, recruit: boolean): void => {
  if (!clientId) return;
  const without = readRecruitViewIds().filter(id => id !== clientId);
  writeJson(RECRUIT_VIEW_KEY, recruit ? [clientId, ...without].slice(0, RECRUIT_VIEW_MEMORY_MAX) : without);
};
